import { GraphQLField, GraphQLInputField } from 'graphql';
import { GraphQLSchema, TypeMap } from 'graphql/type/schema';
import { uniq } from 'lodash';
import { useMemo } from 'react';

import { isSupergraphDirective } from 'src/app/graph/explorerPage/helpers/federationHelpers';

interface UseSchemaPartsArgs {
  graphqlSchema: GraphQLSchema;
  typeMap: TypeMap;
  isSupergraph: boolean;
}
export function useSchemaParts({
  graphqlSchema,
  typeMap,
  isSupergraph,
}: UseSchemaPartsArgs) {
  const { allDirectives, allArguments, allTags } = useMemo(() => {
    const usages = Object.values(typeMap).reduce(
      (acc, type) => {
        const directives = type.astNode?.directives?.slice() ?? [];
        if ('getFields' in type) {
          Object.values(type.getFields()).forEach(
            (field: GraphQLField<unknown, unknown> | GraphQLInputField) => {
              directives.push(...(field.astNode?.directives ?? []));
              if ('args' in field) {
                acc.allArguments.push(...field.args.map((arg) => arg.name));
              }
            },
          );
        }
        directives
          .filter(
            (directive) => !isSupergraphDirective(directive, isSupergraph),
          )
          .forEach((directive) => {
            const directiveName = directive.name.value;
            if (directiveName === 'tag') {
              const tagValue = directive.arguments?.find(
                (arg) => arg.name.value === 'name',
              )?.value;
              if (tagValue?.kind === 'StringValue') {
                acc.allTags.push(tagValue.value);
              }
            }
            acc.allDirectives.push(directiveName);
          });
        return acc;
      },
      {
        allArguments: [] as string[],
        allDirectives: [] as string[],
        allTags: [] as string[],
      },
    );

    graphqlSchema
      .getDirectives()
      .filter((directive) => !isSupergraphDirective(directive, isSupergraph))
      .forEach((directive) => {
        usages.allArguments.push(...directive.args.map((arg) => arg.name));
      });

    return {
      allArguments: uniq(usages.allArguments).sort((a, b) =>
        a.localeCompare(b),
      ),
      allTags: uniq(usages.allTags).sort((a, b) => a.localeCompare(b)),
      allDirectives: uniq(usages.allDirectives).sort((a, b) =>
        a.localeCompare(b),
      ),
    };
  }, [graphqlSchema, isSupergraph, typeMap]);
  const allTypes = useMemo(
    () => Object.keys(typeMap).sort((a, b) => a.localeCompare(b)),
    [typeMap],
  );

  return { allDirectives, allArguments, allTags, allTypes };
}
